/**
 * Pair With — Personalized Pouch add-on (§6.3 / §8.3 custom build).
 *
 * The pouch fields live outside the main product <form> on purpose: rather than
 * hijacking Shopify's native single-item form submission, this listens for the
 * "variant:add" event the theme already dispatches after the pen is added
 * successfully, and — only if a complete pouch selection is pending — fires a
 * second /cart/add.js request for the pouch line item, then re-dispatches the
 * same "cart:change" event the theme's own product-form uses so the cart
 * drawer, cart count, etc. all update exactly as they would for a native add.
 */
class PairWithPouch extends HTMLElement {
  connectedCallback() {
    this.state = 'collapsed';
    this.pendingSelection = null;

    this.collapsedEl = this.querySelector('[data-pwp-collapsed]');
    this.expandedEl = this.querySelector('[data-pwp-expanded]');
    this.addedEl = this.querySelector('[data-pwp-added]');
    this.errorEl = this.querySelector('[data-pwp-error]');
    this.counterEl = this.querySelector('[data-pwp-counter]');
    this.input = this.querySelector('[data-pwp-input]');
    this.swatches = Array.from(this.querySelectorAll('[data-pwp-swatch]'));
    this.addedSwatchEl = this.querySelector('[data-pwp-added-swatch]');
    this.addedTextEl = this.querySelector('[data-pwp-added-text]');

    this.selectedVariantId = null;
    this.selectedColorName = null;

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-pwp-open]');
      const cancel = event.target.closest('[data-pwp-cancel]');
      const confirm = event.target.closest('[data-pwp-confirm]');
      const edit = event.target.closest('[data-pwp-edit]');
      const swatch = event.target.closest('[data-pwp-swatch]');

      if (trigger) this.expand();
      if (cancel) this.collapse();
      if (confirm) this.confirm();
      if (edit) this.expand();
      if (swatch) this.selectSwatch(swatch);
    });

    if (this.input) {
      this.input.addEventListener('input', () => this.updateCounter());
      this.updateCounter();
    }

    document.addEventListener('variant:add', (event) => this.onPenAdded(event));
  }

  setState(state) {
    this.state = state;
    this.collapsedEl.hidden = state !== 'collapsed';
    this.expandedEl.hidden = state !== 'expanded';
    this.addedEl.hidden = state !== 'added';

    document.dispatchEvent(
      new CustomEvent('pair-with-pouch:state', {
        detail: { blocked: state === 'expanded' },
      })
    );
  }

  expand() {
    this.setError('');
    this.setState('expanded');
  }

  collapse() {
    this.setError('');
    this.setState(this.pendingSelection ? 'added' : 'collapsed');
  }

  selectSwatch(swatch) {
    this.swatches.forEach((el) => el.setAttribute('aria-pressed', 'false'));
    swatch.setAttribute('aria-pressed', 'true');
    this.selectedVariantId = swatch.getAttribute('data-pwp-swatch');
    this.selectedColorName = swatch.getAttribute('data-pwp-color-name');
    this.setError('');
  }

  updateCounter() {
    if (!this.counterEl || !this.input) return;
    const max = this.input.getAttribute('maxlength') || 16;
    this.counterEl.textContent = `${this.input.value.length}/${max}`;
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  confirm() {
    const monogram = (this.input?.value || '').trim();

    if (!this.selectedVariantId || monogram === '') {
      this.setError('Enter a monogram, or type BLANK to skip personalization.');
      return;
    }

    this.pendingSelection = {
      variantId: this.selectedVariantId,
      colorName: this.selectedColorName,
      monogram,
    };

    if (this.addedSwatchEl) {
      const activeSwatch = this.swatches.find((el) => el.getAttribute('aria-pressed') === 'true');
      this.addedSwatchEl.style.backgroundColor = activeSwatch ? activeSwatch.style.backgroundColor : '';
    }

    if (this.addedTextEl) {
      this.addedTextEl.innerHTML = `<strong>${this.escapeHtml(this.selectedColorName)}</strong> &middot; ${this.escapeHtml(monogram)}`;
    }

    this.setState('added');
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  async onPenAdded(event) {
    if (this.state !== 'added' || !this.pendingSelection) return;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = new FormData();
    body.append('id', this.pendingSelection.variantId);
    body.append('quantity', 1);
    body.append('properties[Color]', this.pendingSelection.colorName);
    body.append('properties[Personalization]', this.pendingSelection.monogram);
    body.append('sections', sectionsToBundle.join(','));
    body.append('sections_url', `${Shopify.routes.root}variants/${this.pendingSelection.variantId}`);

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      body,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    const responseJson = await response.json();
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent.sections = responseJson.sections;

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: event.detail?.onSuccessDo,
          cart: cartContent,
        },
      })
    );

    this.pendingSelection = null;
    this.selectedVariantId = null;
    this.selectedColorName = null;
    this.swatches.forEach((el) => el.setAttribute('aria-pressed', 'false'));
    if (this.input) this.input.value = '';
    this.updateCounter();
    this.setState('collapsed');
  }
}

if (!window.customElements.get('pair-with-pouch')) {
  window.customElements.define('pair-with-pouch', PairWithPouch);
}

/**
 * Mute the main Add to Cart button while the pouch card is open but
 * unresolved, per §6.2: "Stays disabled/muted if the pouch add-on has been
 * opened but left incomplete." Scoped to a document-level listener rather
 * than editing buy-buttons.liquid, so this stays isolated to this feature.
 */
document.addEventListener('pair-with-pouch:state', (event) => {
  const submitButton = document.querySelector('product-form form[action*="/cart/add"] [type="submit"]');
  if (!submitButton) return;

  submitButton.disabled = event.detail.blocked;
  submitButton.classList.toggle('is-muted', event.detail.blocked);
  submitButton.title = event.detail.blocked
    ? 'Finish or close the Personalized Pouch selection above to continue.'
    : '';
});
